import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCartItems, updateCartItemSize, updateCartItemStockType } from '../../api/products';
import Button from '../../components/Button';
import CartItemCard from '../../components/CartItemCard';
import AlertModal from '../../components/AlertModal';
import { formatRublesForUser } from '../../utils/formatRubles';
import { track } from '../../utils/productAnalytics';
import './CartPage.css';

const DEFAULT_CHECKOUT_STOCK_MISS_MESSAGE =
  'Товара не оказалось на складе — возможно, кто-то купил его быстрее вас. Сожалеем, что так вышло. Вы можете оформить его под заказ из Китая: откройте вкладку «Под заказ» и оформите там.';

export default function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPreorderIds, setSelectedPreorderIds] = useState(() => new Set());
  const [selectedInStockIds, setSelectedInStockIds] = useState(() => new Set());
  const [activeTab, setActiveTab] = useState('preorder'); // 'preorder' | 'in_stock'
  const [toast, setToast] = useState(null);
  const [stockMissModal, setStockMissModal] = useState(null);
  const cartViewTrackedRef = useRef(false);

  const loadCart = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await getCartItems();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!silent) {
        setError(e.message || 'Не удалось загрузить корзину');
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    if (!location.state?.checkoutStockMiss) return;
    const raw = location.state.checkoutStockMissMessage;
    const msg =
      typeof raw === 'string' && raw.trim() ? raw.trim() : DEFAULT_CHECKOUT_STOCK_MISS_MESSAGE;
    setActiveTab('preorder');
    setStockMissModal({ message: msg });
    navigate('.', { replace: true, state: {} });
    loadCart({ silent: true });
  }, [location.state, navigate, loadCart]);

  useEffect(() => {
    if (loading || cartViewTrackedRef.current) return;
    cartViewTrackedRef.current = true;
    track('cart_view', {
      lines: items.length,
      tab: activeTab,
    });
  }, [loading, items.length, activeTab]);

  useEffect(() => {
    const onInvalidate = () => loadCart({ silent: true });
    window.addEventListener('miniapp-invalidate-cart', onInvalidate);
    return () => window.removeEventListener('miniapp-invalidate-cart', onInvalidate);
  }, [loadCart]);

  const handleSelect = (cartItemId) => {
    const updater = activeTab === 'preorder' ? setSelectedPreorderIds : setSelectedInStockIds;
    updater((prev) => {
      const next = new Set(prev);
      if (next.has(cartItemId)) next.delete(cartItemId);
      else next.add(cartItemId);
      return next;
    });
  };

  const handleDeleted = (deletedId) => {
    setSelectedPreorderIds((prev) => {
      const next = new Set(prev);
      next.delete(deletedId);
      return next;
    });
    setSelectedInStockIds((prev) => {
      const next = new Set(prev);
      next.delete(deletedId);
      return next;
    });
    loadCart();
  };

  const handleQuantityChange = (cartItem, newQuantity, reason) => {
    if (reason === 'stock_limit') {
      setToast('Нельзя добавить больше, чем есть на складе для этого размера');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    if (reason === 'preorder_limit') {
      setToast('Нельзя заказать более 50 единиц под заказ');
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setItems((prev) => {
      if (newQuantity <= 0) {
        return prev.filter((it) => it.id !== cartItem.id);
      }
      return prev.map((it) =>
        it.id === cartItem.id ? { ...it, quantity: newQuantity } : it
      );
    });
  };

  const handleSizeChange = async (cartItem, newSize) => {
    const normalized = (newSize || '').trim();
    if (!normalized || normalized === (cartItem.size || '')) return;
    // Оптимистично обновляем локальное состояние: либо меняем размер, либо объединяем с существующей позицией
    setItems((prev) => {
      const itemsCopy = [...prev];
      const idx = itemsCopy.findIndex((it) => it.id === cartItem.id);
      if (idx === -1) return prev;
      const current = itemsCopy[idx];

      const same = itemsCopy.find(
        (it) =>
          it.id !== current.id &&
          it.item.id === current.item.id &&
          (it.size || '') === normalized &&
          it.stock_type === current.stock_type,
      );

      if (same) {
        same.quantity += current.quantity;
        itemsCopy.splice(idx, 1);
      } else {
        itemsCopy[idx] = { ...current, size: normalized };
      }
      return itemsCopy;
    });

    try {
      await updateCartItemSize(cartItem.id, normalized);
    } catch (e) {
      setError(e.message || 'Не удалось изменить размер');
      // В случае ошибки можно перезагрузить корзину, чтобы вернуть корректное состояние
      loadCart();
    }
  };

  const handleStockTypeChange = async (cartItem, newType, hasStockForCurrentSize) => {
    if (newType === cartItem.stock_type) return;

    if (newType === 'in_stock' && !hasStockForCurrentSize) {
      setToast('Данного размера нет в наличии');
      setTimeout(() => setToast(null), 3000);
      return;
    }

    // Оптимистичное обновление локального состояния с возможным объединением
    setItems((prev) => {
      const itemsCopy = [...prev];
      const idx = itemsCopy.findIndex((it) => it.id === cartItem.id);
      if (idx === -1) return prev;
      const current = itemsCopy[idx];

      const same = itemsCopy.find(
        (it) =>
          it.id !== current.id &&
          it.item.id === current.item.id &&
          (it.size || '') === (current.size || '') &&
          it.stock_type === newType,
      );

      if (same) {
        same.quantity += current.quantity;
        itemsCopy.splice(idx, 1);
      } else {
        itemsCopy[idx] = { ...current, stock_type: newType };
      }

      // Чистим выбранные id, которые больше не существуют или сменили тип
      const validIdsPre = new Set(
        [...selectedPreorderIds].filter((id) => {
          const it = itemsCopy.find((x) => x.id === id);
          return it && it.stock_type === 'preorder';
        }),
      );
      const validIdsIn = new Set(
        [...selectedInStockIds].filter((id) => {
          const it = itemsCopy.find((x) => x.id === id);
          return it && it.stock_type === 'in_stock';
        }),
      );
      setSelectedPreorderIds(validIdsPre);
      setSelectedInStockIds(validIdsIn);

      return itemsCopy;
    });

    try {
      await updateCartItemStockType(cartItem.id, newType);
    } catch (e) {
      setError(e.message || 'Не удалось изменить тип заказа');
      loadCart();
    }
  };

  const filteredItems = useMemo(
    () => items.filter((it) => it.stock_type === (activeTab === 'preorder' ? 'preorder' : 'in_stock')),
    [items, activeTab],
  );

  const selectedIds = activeTab === 'preorder' ? selectedPreorderIds : selectedInStockIds;

  const totalPrice = useMemo(() => {
    if (!filteredItems.length || !selectedIds.size) return 0;
    return filteredItems.reduce((sum, cartItem) => {
      if (!selectedIds.has(cartItem.id)) return sum;
      const item = cartItem.item || {};
      let pricePerUnit = null;
      if (cartItem.stock_type === 'in_stock' && item.fixed_price_rub != null) {
        pricePerUnit = Number(item.fixed_price_rub);
      } else {
        pricePerUnit = Number(cartItem.price_rub ?? item.price_rub ?? 0);
      }
      if (!Number.isFinite(pricePerUnit)) return sum;
      return sum + pricePerUnit * (cartItem.quantity || 1);
    }, 0);
  }, [filteredItems, selectedIds]);

  return (
    <div className="cart-page">
      {stockMissModal && (
        <AlertModal
          title="Нет в наличии"
          message={stockMissModal.message}
          confirmLabel="Понятно"
          onClose={() => setStockMissModal(null)}
        />
      )}
      <div className="cart-page__inner page-container">
        {toast && (
          <div className="cart-page__toast">
            <div className="cart-page__toast-inner">
              {toast}
            </div>
          </div>
        )}
        <div className="cart-page__header">
          <button
            type="button"
            className={`cart-page__tab ${activeTab === 'preorder' ? 'cart-page__tab--active' : ''}`}
            onClick={() => setActiveTab('preorder')}
          >
            Под заказ
          </button>
          <button
            type="button"
            className={`cart-page__tab ${activeTab === 'in_stock' ? 'cart-page__tab--active' : ''}`}
            onClick={() => setActiveTab('in_stock')}
          >
            Из наличия
          </button>
        </div>

        {loading && (
          <p className="cart-page__loading">Загрузка…</p>
        )}
        {error && (
          <p className="cart-page__error" role="alert">{error}</p>
        )}
        {!loading && !error && filteredItems.length === 0 && (
          <p className="cart-page__empty">В корзине пока ничего нет</p>
        )}
        {!loading && !error && filteredItems.length > 0 && (
          <ul className="cart-page__list">
            {filteredItems.map((cartItem) => (
              <li key={cartItem.id} className="cart-page__list-item">
                <CartItemCard
                  cartItem={cartItem}
                  selected={selectedIds.has(cartItem.id)}
                  onSelect={handleSelect}
                  onQuantityChange={handleQuantityChange}
                  onDeleted={handleDeleted}
                  onSizeChange={handleSizeChange}
                  onStockTypeChange={handleStockTypeChange}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {totalPrice > 0 && selectedIds.size > 0 && (
        <div className="cart-page__checkout">
          <div className="cart-page__checkout-inner">
            <div className="cart-page__checkout-bg" />
            <div className="cart-page__checkout-content">
              <div className="cart-page__checkout-price-wrap">
                <span className="cart-page__checkout-price">
                  {formatRublesForUser(totalPrice)}
                </span>
              </div>
              <div className="cart-page__checkout-btn-wrap">
                <Button
                  size="small"
                  variant="primary"
                  className="cart-page__checkout-btn"
                  onClick={() => {
                    const ids = Array.from(selectedIds);
                    track('cart_checkout_click', {
                      selected_count: ids.length,
                      tab: activeTab,
                    });
                    navigate('/main/checkout', {
                      state: { selectedCartItemIds: ids },
                    });
                  }}
                >
                  Перейти к оформлению
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
